import { useId, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import { CrosshairIcon, FilmIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Field } from './Field';
import { ImageField } from './ImageField';
import { ProductImage } from '../../ui/ProductImage';
import { inputCls } from './styles';
import { MEDIA_ALT_MAX, clampFocus, focusPosition, mediaProblem } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
type Props = {
  label: string;
  hint?: string;
  value: Media;
  onChange: (value: Media) => void;
  /** Of een video hier past. Productgalerij en sfeerbeelden: ja. */
  allowVideo?: boolean;
  /** Wat er staat als het leeg blijft, in woorden. */
  emptyNote?: string;
};

/** Hoogstens zoveel MB video in de demo; de server neemt tot 150 MB aan (LARAVEL.md §29). */
const MAX_VIDEO_MB = 150;
/** Zoveel procent per pijltjestoets bij het belangrijkste punt. */
const FOCUS_STEP = 5;
const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1]
};

/**
 * Eén beeld voor de site: foto (slepen, kiezen of link), beschrijving, het belangrijkste punt
 * en optioneel een korte video. Rechts zie je meteen hoe het op telefoon (staand) en
 * computer (liggend) wordt uitgesneden.
 *
 * Video in de demo is een tijdelijke link (alleen deze sessie). Met de server: uploaden naar
 * `POST /admin/media`, die een 1080p- en 720p-versie en een poster maakt.
 */
export function MediaField({
  label,
  hint,
  value,
  onChange,
  allowVideo = true,
  emptyNote = 'Leeg: de standaardfoto van deze plek.'
}: Props) {
  const videoRef = useRef<HTMLInputElement>(null);
  const altId = useId();
  const set = (patch: Partial<Media>) => onChange({
    ...value,
    ...patch
  });
  const problem = mediaProblem(value);
  const position = focusPosition(value.focus) ?? '50% 50%';
  const focus = value.focus ?? {
    x: 50,
    y: 50
  };
  const pickFocus = (e: MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    set({
      focus: clampFocus({
        x: (e.clientX - r.left) / r.width * 100,
        y: (e.clientY - r.top) / r.height * 100
      })
    });
  };
  const nudgeFocus = (e: KeyboardEvent<HTMLButtonElement>) => {
    const d = ARROWS[e.key];
    if (!d) return;
    e.preventDefault();
    set({
      focus: clampFocus({
        x: focus.x + d[0] * FOCUS_STEP,
        y: focus.y + d[1] * FOCUS_STEP
      })
    });
  };
  const takeVideo = (file?: File) => {
    if (!file) return;
    if (!/^video\/(mp4|quicktime)$/.test(file.type) || file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error('Deze video kan niet worden gebruikt', {
        description: `Kies een mp4 of mov van maximaal ${MAX_VIDEO_MB} MB en hoogstens een minuut.`
      });
      return;
    }
    set({
      video: {
        src: URL.createObjectURL(file)
      }
    });
    toast('Video staat klaar', {
      description: 'In de demo alleen in deze sessie. Met de server wordt hij opgeslagen in je mediabibliotheek.'
    });
  };
  return <Field label={label} hint={hint} as="div" data-id="element-5021" data-mp="2jx4aay65fopq">
      <div className="grid gap-5 md:grid-cols-[minmax(0,14rem)_1fr]" data-id="element-5022" data-mp="zdr10zv5o096">
        <div className="space-y-3" data-id="element-5023" data-mp="zfliu5b4rc91">
          <ImageField value={value.image} onChange={image => set({
          image
        })} fit="cover" aspect="aspect-[4/3]" data-id="element-5024" data-mp="14d3jl8ezul5y" />
          {!value.image && <p className="text-[12px] text-muted" data-id="element-5025" data-mp="pn63lfr7x1ru">{emptyNote}</p>}
        </div>

        <div className="min-w-0 space-y-4" data-id="element-5026" data-mp="um0nkl0x8wdj">
          <label className="block" htmlFor={altId} data-id="element-5027" data-mp="ukpeq6smcyfk">
            <span className="flex items-baseline justify-between gap-3" data-id="element-5028" data-mp="gmrcu0vhiugy">
              <span className="text-[13px] font-medium text-ink" data-id="element-5029" data-mp="kocq3nbxlaro">Wat is er te zien?</span>
              <span className={`text-[12px] tabular-nums ${value.alt.length > MEDIA_ALT_MAX ? 'text-clay' : 'text-muted'}`} data-id="element-5030" data-mp="6otiqwqy23gf">
                {value.alt.length} / {MEDIA_ALT_MAX}
              </span>
            </span>
            <span className="mt-0.5 block text-[12px] text-muted" data-id="element-5031" data-mp="212cmwpfhckap">Voor blinde bezoekers en Google. Leeg = alleen sfeer.</span>
            <input id={altId} value={value.alt} onChange={e => set({
            alt: e.target.value
          })} className={`${inputCls} mt-1.5`} data-id="element-5032" data-mp="260mhoyxalivs" />
          </label>

          {value.image && <div data-id="element-5033" data-mp="10388ohlntlf">
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-5034" data-mp="11o36f7ttejt">
                <CrosshairIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5035" data-mp="3t3ckp6c563y3" />
                Belangrijkste punt
              </p>
              <p className="mt-0.5 text-[12px] text-muted" data-id="element-5036" data-mp="3o7nkr1w7ml19">Klik op wat altijd in beeld moet blijven (of gebruik de pijltjes).</p>
              <div className="mt-2 flex flex-wrap items-start gap-3" data-id="element-5037" data-mp="3eezp4agyun44">
                <button type="button" onClick={pickFocus} onKeyDown={nudgeFocus} aria-label={`Belangrijkste punt: ${focus.x}% van links, ${focus.y}% van boven. Klik of gebruik de pijltjes om te verplaatsen.`} className="relative h-28 w-28 shrink-0 cursor-crosshair overflow-hidden rounded-md bg-sand" data-id="element-5038" data-mp="39geklyuolh9v">
                  <ProductImage src={value.image} fit="contain" className="h-full w-full" data-id="element-5039" data-mp="1ayuy4762wrlm" />
                  <span aria-hidden="true" className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-shell bg-clay shadow-knob" style={{
                left: `${focus.x}%`,
                top: `${focus.y}%`
              }} data-id="element-5040" data-mp="1fxpdez4u7y0z" />
                </button>
                {/* Zo wordt het uitgesneden op de site. */}
                <figure className="w-[4.5rem]" data-id="element-5041" data-mp="s60hz9wpytft">
                  <div className="aspect-[4/5] overflow-hidden rounded-sm bg-sand" data-id="element-5042" data-mp="x6eryxhvsdgh">
                    <ProductImage src={value.image} fit="cover" position={position} className="h-full w-full" data-id="element-5043" data-mp="1191jsyejxydo" />
                  </div>
                  <figcaption className="mt-1 text-[11px] text-muted" data-id="element-5044" data-mp="160b9u040j3q3">Telefoon</figcaption>
                </figure>
                <figure className="w-40" data-id="element-5045" data-mp="9hdcwrejm428">
                  <div className="aspect-[16/9] overflow-hidden rounded-sm bg-sand" data-id="element-5046" data-mp="diyzx5ksjyq5">
                    <ProductImage src={value.image} fit="cover" position={position} className="h-full w-full" data-id="element-5047" data-mp="1gu4ouyujx9y5" />
                  </div>
                  <figcaption className="mt-1 text-[11px] text-muted" data-id="element-5048" data-mp="1bvtb4d14yfan">Computer</figcaption>
                </figure>
              </div>
            </div>}

          {allowVideo && <div className="border-t border-line pt-4" data-id="element-5049" data-mp="1lkl0qeiq4nie">
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-5050" data-mp="1lmxq3cqta05h">
                <FilmIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5051" data-mp="xxcw7yt4dihf" />
                Korte video (optioneel)
              </p>
              <p className="mt-0.5 text-[12px] text-muted" data-id="element-5052" data-mp="16w6xw3lb4z1u">
                Een stille loop van 6 tot 15 seconden. Speelt niet voor wie minder beweging wil; dan staat de foto er.
              </p>
              {value.video ? <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-5053" data-mp="2i9ize7tlh5ku">
                  <video src={value.video.src} muted loop playsInline autoPlay className="h-20 w-32 rounded-sm bg-sand object-cover" aria-hidden="true" data-id="element-5054" data-mp="2e8gwnh0gybe4" />
                  <button type="button" onClick={() => onChange({
              image: value.image,
              alt: value.alt,
              ...(value.focus ? {
                focus: value.focus
              } : {})
            })} className="inline-flex items-center gap-1.5 rounded-md px-2 py-2 text-[13px] text-clay transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-5055" data-mp="2lvdwg0j4igdo">
                    <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5056" data-mp="34kasa8u65gy0" />
                    Video weghalen
                  </button>
                </div> : <div className="mt-2 flex min-w-0 gap-2" data-id="element-5057" data-mp="2utfwacr4f2zd">
                  <input placeholder="Plak een link naar een mp4" aria-label="Link naar een video" className={inputCls} onBlur={e => {
              const src = e.target.value.trim();
              if (src) set({
                video: {
                  src
                }
              });
            }} data-id="element-5058" data-mp="2zs0xofi3zspe" />
                  <button type="button" onClick={() => videoRef.current?.click()} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-white px-3 text-[13px] text-ink transition-colors duration-150 ease-soft hover:border-ink/40" data-id="element-5059" data-mp="1ng5igq55834q">
                    <UploadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5060" data-mp="2b4om2sd8zrqh" />
                    Kies
                    <span className="sr-only" data-id="element-5061" data-mp="274mkskbfd0bq"> een video van je apparaat</span>
                  </button>
                  <input ref={videoRef} type="file" accept="video/mp4,video/quicktime" className="hidden" tabIndex={-1} aria-hidden="true" onChange={e => takeVideo(e.target.files?.[0])} data-id="element-5062" data-mp="22370iuh2mxue" />
                </div>}
            </div>}

          {problem && <p role="alert" className="text-[13px] text-clay" data-id="element-5063" data-mp="glog64erohms">
              {problem}
            </p>}
        </div>
      </div>
    </Field>;
}